import type { KeldaConfigDto, StorageFolder } from '@mimir/common/dto/keldaConfigDto';
import { KELDA_SERVE_BUCKET, KELDA_BUCKET_ROOT } from '@mimir/common/dto/keldaConfigPrimitives';
import type { KeldaStatusDto } from '@mimir/common/dto/statusDto';
import type { StorageType } from '@mimir/common/dto/storageModel';
import { getDefaultKeldaStorageId } from '@mimir/common/dto/storageModel';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

export const doesConfigSupportCentralConform = (conf: KeldaConfigDto) => {
  return conf.useCentralConform === true && conf.amePresets && conf.amePresets.length > 0;
};

export function getServerFolderPath(keldaConfig: KeldaConfigDto) {
  return keldaConfig.serverFolder ?? `${KELDA_BUCKET_ROOT}/${KELDA_SERVE_BUCKET}`;
}

export type StorageFolderTreeNode = {
  name: string,
  displayName?: string,
  isPrimaryRoot?: boolean
};

export function getAvailableStorageFolders(keldaConfig: KeldaConfigDto, status: KeldaStatusDto | null): StorageFolderTreeNode[] {
  const serveBucket = {
    name: getServerFolderPath(keldaConfig),
    displayName: 'Kelda primary storage',
    isPrimaryRoot: true
  };
  return [serveBucket, ...(status?.storageFolders ?? [])];
}

export function getStorageFolderPath(sf: StorageFolder) {
  return `${sf.rootFolder ? `${sf.rootFolder}/` : ''}${sf.relativePath}`;
}

export function getStorageFolderFromConfig(name: string | null, keldaConfig: KeldaConfigDto) {
  if (!name) return undefined;
  return keldaConfig.storageFolders?.find(f => getStorageFolderPath(f) === name);
}

function getStorageTypeForFolderFromConfig(keldaId: string, folder: StorageFolder, owner: string): StorageType {
  return {
    id: folder.id ?? getDefaultKeldaStorageId(keldaId, folder.relativePath),
    keldaId,
    name: folder.title || folder.relativePath,
    private: false,
    owner,
    sidecarXmlKind: folder.sidecarXmlKind,
    attachmentToTransfer: folder.attachmentToTransfer,
    visibleTo: folder.visibleTo,
  };
}

export function getActiveStorageFolders(keldaConfig: KeldaConfigDto, status: KeldaStatusDto | undefined, owner: string) {
  if (!status) {
    const storageFolders = keldaConfig.storageFolders;
    return storageFolders ? storageFolders.map(f => getStorageTypeForFolderFromConfig(keldaConfig.id, f, owner)) : [];
  }

  const availableFolders = getAvailableStorageFolders(keldaConfig, status);
  const activeFolders: StorageType[] = [];

  for (const available of availableFolders) {
    const folderInConfig = getStorageFolderFromConfig(available.name, keldaConfig);
    if (folderInConfig) {
      activeFolders.push(getStorageTypeForFolderFromConfig(keldaConfig.id, folderInConfig, owner));
    }
  }

  return activeFolders;
}

export const getSanitizedStorageFolderConfig = (selectedConfig: StorageFolder): StorageFolder | undefined => {
  const clone = cloneDeep(selectedConfig);
  // Replacing 'h264|high' and 'h264|main' with just 'h264' since we don't differentiate anymore
  const targetFormat = clone.targetMediaFormat?.video;
  if (!targetFormat) return;

  if ('codec' in targetFormat && targetFormat.codec.startsWith('h264')) {
    targetFormat.codec = 'h264';
  }

  clone.useItemTitleAsFileName = clone.useItemTitleAsFileName ?? false;

  if (clone.useItemTitleAsFileName && !clone.fileNameTemplateString) {
    clone.fileNameTemplateString = DEFAULT_FILENAME_TEMPLATE;
  }

  return clone;
};

export const DEFAULT_FILENAME_TEMPLATE = '{title}';

export function normalizeChannelList(channels: string) {
  const list = channels.split(',');
  const channelList: string[] = [];

  list.forEach((value) => {
    const valueWithoutSpace = value.replace(/ /g, '');
    if (!isEmpty(valueWithoutSpace)) {
      channelList.push(valueWithoutSpace);
    }
  });

  return [...new Set(channelList)];
}
